import React, { useState, useEffect, memo } from "react";
import {
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Drawer,
  CircularProgress,
  IconButton,
  Slider,
  Alert,
} from "@mui/material";
import HLSPlayer from "./HLSPlayer"; // your HLS player component
import VideoLibraryOutlinedIcon from "@mui/icons-material/VideoLibraryOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { S3Client, GetObjectCommand } from "@aws-sdk/client-s3";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import logo from "../static/logo.png";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import KeyMoments from "./KeyMoments";
import { useTheme } from "@emotion/react";
import {
  AccountCircle,
  PauseCircleFilledOutlined,
  PlayCircleFilledOutlined,
  RotateLeft,
  SetMealRounded,
  VolumeMuteOutlined,
  VolumeOffOutlined,
} from "@mui/icons-material";
import ReactPlayer from "react-player";

const KeySegmentsNew = (props) => {
  const theme = useTheme();
  const currentKeySegment = useSelector(
    (state) => state.keysegmentsAndStorylines.currentKeysegment
  );
  const { socketComplete } = props;
  const identityPoolId = process.env.REACT_APP_IDENTITY_POOL_ID;
  const user = sessionStorage.getItem('username')?.split('@')[0];
  const username = user.charAt(0).toUpperCase() + user.slice(1);

  const queryTextRedux = useSelector(state => state.queryText.value);
  const queryType = useSelector(state => state.queryText.queryType);

  const [updatedCurrentKeySegment, setUpdatedCurrentKeySegment] =
    useState(null);

  const [keyMoments, setKeyMoments] = useState([]);
  const [currentKeyMoment, setCurrentKeyMoment] = useState(null);
  const [sliderValue, setSliderValue] = useState([]);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [mute, setMute] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);

  // Function to fetch the HLS manifest for a single video.
  const fetchHLSManifestForVideo = async (segment) => {
    const bucketName = segment?.video_s3_bucket;
    const objectKey = segment?.video_s3_key;
    try {
      const idToken = sessionStorage.getItem("idToken");
      if (!idToken) {
        console.error("No idToken found in sessionStorage.");
        return segment;
      }

      const s3Client = new S3Client({
        region: process.env.REACT_APP_AWS_REGION,
        credentials: fromCognitoIdentityPool({
          clientConfig: { region: process.env.REACT_APP_AWS_REGION },
          identityPoolId: identityPoolId,
          logins: {
            [`cognito-idp.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${process.env.REACT_APP_USER_POOL_ID}`]:
              idToken,
          },
        }),
      });

      const command = new GetObjectCommand({
        Bucket: bucketName,
        Key: objectKey,
      });
      if (objectKey?.endsWith(".m3u8")) {
        const data = await s3Client.send(command);
        const manifestText = await new Response(data.Body).text();
        const lines = manifestText.split("\n");

        // Update lines that reference .ts segments with presigned URLs
        const updatedLines = await Promise.all(
          lines.map(async (line) => {
            if (line.trim().endsWith(".ts")) {
              const presignedUrl = await generatePresignedUrlForTs(
                line.trim(),
                s3Client,
                segment
              );
              return presignedUrl;
            }
            return line;
          })
        );

        const updatedManifestText = updatedLines.join("\n");

        const updatedBlob = new Blob([updatedManifestText], {
          type: "application/vnd.apple.mpegurl",
        });
        const updatedManifestUrl = window.URL.createObjectURL(updatedBlob);
        return updatedManifestUrl;
      } else {
        const signedUrl = await getSignedUrl(s3Client, command, {
          expiresIn: 3600,
        });

        return signedUrl;
      }
    } catch (error) {
      console.error(
        "Error fetching HLS manifest from S3 for video id",
        segment.id,
        error
      );
      return undefined;
    }
  };

  const generatePresignedUrlForTs = async (tsFileKey, s3Client, segment) => {
    let formattedTsFileKey = tsFileKey.replace(/%20/g, " ");

    // let _tsFileKey = segment.video_s3_key
    //   .split("/")
    //   .slice(3, 4)
    //   .concat([formattedTsFileKey])
    //   .join("/");

    let _key = formattedTsFileKey?.split("/")?.slice(3)?.join("/");
    let _bucketName = formattedTsFileKey?.split("/")?.slice(2, 3);

    const command = new GetObjectCommand({
      // Bucket: segment.video_s3_bucket,
      Bucket: _bucketName[0],
      Key: _key,
    });
    const presignedUrl = await getSignedUrl(s3Client, command, {
      expiresIn: 3600,
    });
    return presignedUrl;
  };

  const fetchURL = async () => {
    let s3FinalUrl = await fetchHLSManifestForVideo(currentKeySegment);
    setUpdatedCurrentKeySegment({
      ...currentKeySegment,
      url: s3FinalUrl ?? "",
    });
  };

  const formattedDate = (timestamp) => {
    return new Date(timestamp).toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: true,
    });
  };

  const formatIntervalLabel = (value) => {
    if (!value) return "00:00";
    const minutes = Math.floor(value / 60); // Get full minutes
    const seconds = Math.floor(value % 60); // Get remaining seconds

    return `${minutes < 10 ? "0" : ""}${minutes}:${
      seconds < 10 ? "0" : ""
    }${seconds}`;
  };

  const playPauseHandler = () => {
    setPlaying((val) => !val);
  };

  const muteHandler = () => {
    setMute((val) => !val);
  };

  const handleRangeSlider = (event, newValue) => {
    if (currentKeyMoment) {
      setSliderValue(newValue);
      setStartTime(newValue[0]);
      setEndTime(newValue[1]);
    } else {
      setSliderValue(newValue);
      setStartTime(newValue[0]);
      setEndTime(newValue[1]);
    }
    setPlaying(false);
  };

  const handleResetInterval = () => {
    setPlaying(false);
    if (currentKeyMoment) {
      let s_time;
      let e_time;
      if (currentKeySegment?.video_s3_key?.includes("m3u8")) {
        s_time = Math.floor(
          currentKeyMoment.start_time / 1000 +
            currentKeySegment?.video_start_time / 1000
        );
        e_time = Math.floor(
          currentKeyMoment.end_time / 1000 +
            currentKeySegment?.video_start_time / 1000
        );
      } else {
        s_time = Math.floor(currentKeyMoment.start_time / 1000);
        e_time = Math.floor(currentKeyMoment.end_time / 1000);
      }
      setSliderValue([
        Math.floor(currentKeyMoment.start_time / 1000),
        Math.floor(currentKeyMoment.end_time / 1000),
      ]);
      setStartTime(s_time);
      setEndTime(e_time);
    } else {
      if (currentKeySegment?.video_s3_key?.includes("m3u8")) {
        setStartTime(Math.floor(currentKeySegment?.video_start_time / 1000));
        setEndTime(Math.floor(currentKeySegment?.video_end_time / 1000));
        setSliderValue([
          0,
          Math.floor(currentKeySegment?.video_duration / 1000),
        ]);
      } else {
        setStartTime(Math.floor(0));
        setEndTime(Math.floor(currentKeySegment?.video_duration / 1000));
        setSliderValue([
          0,
          Math.floor(currentKeySegment?.video_duration / 1000),
        ]);
      }
    }
  };

  const onChangeKeyMoment = (index) => {
    setPlaying(false);
    if (keyMoments[index]?.description === currentKeyMoment?.description) {
      if (currentKeySegment?.video_s3_key?.includes("m3u8")) {
        setStartTime(Math.floor(currentKeySegment?.video_start_time / 1000));
        setEndTime(Math.floor(currentKeySegment?.video_end_time / 1000));
        setSliderValue([
          0,
          Math.floor(currentKeySegment?.video_end_time / 1000),
        ]);
        setVideoDuration(Math.floor(currentKeySegment?.video_duration / 1000));
      } else {
        setStartTime(0);
        setEndTime(Math.floor(currentKeySegment?.video_duration / 1000));
        setSliderValue([
          0,
          Math.floor(currentKeySegment?.video_duration / 1000),
        ]);
        setVideoDuration(Math.floor(currentKeySegment?.video_duration / 1000));
      }
      setCurrentKeyMoment(null);
      setCurrentTime(0);
    } else {
      let s_time;
      let e_time;
      if (currentKeySegment?.video_s3_key?.includes("m3u8")) {
        s_time = Math.floor(
          keyMoments[index].start_time / 1000 +
            currentKeySegment?.video_start_time / 1000
        );
        e_time = Math.floor(
          keyMoments[index].end_time / 1000 +
            currentKeySegment?.video_start_time / 1000
        );
      } else {
        s_time = Math.floor(keyMoments[index].start_time / 1000);
        e_time = Math.floor(keyMoments[index].end_time / 1000);
      }
      setSliderValue([
        Math.floor(keyMoments[index].start_time / 1000),
        Math.floor(keyMoments[index].end_time / 1000),
      ]);
      setStartTime(s_time);
      setEndTime(e_time);
      setCurrentKeyMoment(keyMoments[index]);
      setCurrentTime(Math.floor(keyMoments[index].start_time / 1000));
      setVideoDuration(Math.floor(keyMoments[index].end_time / 1000));
    }
    // setPlaying(false);
    // setPlaytime(Math.floor(keyMoments[index].start_time / 1000));
  };

  const handleUpdateCurrentTime = (value) => {
    if (currentKeyMoment) {
      let time =
        Math.floor(value) + Math.floor(currentKeyMoment?.start_time / 1000);
      setCurrentTime(time);
      if (time >= videoDuration) {
        // setPlaying(false);
        // setStartTime(
        //   Math.floor(
        //     currentKeyMoment?.start_time / 1000 +
        //       currentKeySegment?.video_start_time / 1000
        //   )
        // );
        setCurrentTime(Math.floor(currentKeyMoment.start_time / 1000));
      }
    } else {
      setCurrentTime(value);
      if (value >= videoDuration) {
        // setPlaying(false);
        setCurrentTime(0);
        // setStartTime(Math.floor(currentKeySegment?.video_start_time / 1000));
      }
    }
  };

  const handleAutoPause = () => {
    setPlaying(false);
  };

  useEffect(() => {
    if (currentKeySegment !== null && currentKeySegment !== undefined) {
      if (currentKeySegment?.video_s3_key?.includes("m3u8")) {
        setStartTime(Math.floor(currentKeySegment?.video_start_time / 1000));
        setEndTime(Math.floor(currentKeySegment?.video_end_time / 1000));
        setKeyMoments(currentKeySegment?.key_moments);
        setSliderValue([
          0,
          Math.floor(currentKeySegment?.video_duration / 1000),
        ]);
        setVideoDuration(Math.floor(currentKeySegment?.video_duration / 1000));
        setCurrentKeyMoment(null);
      } else {
        setStartTime(0);
        setEndTime(Math.floor(currentKeySegment?.video_duration / 1000));
        setKeyMoments(currentKeySegment?.key_moments);
        setSliderValue([
          0,
          Math.floor(currentKeySegment?.video_duration / 1000),
        ]);
        setVideoDuration(Math.floor(currentKeySegment?.video_duration / 1000));
        setCurrentKeyMoment(null);
      }
      fetchURL();
    }
  }, [currentKeySegment]);

  useEffect(() => {
    setUpdatedCurrentKeySegment(null);
  }, [queryTextRedux]);

  return (
    <Box
      height={"100%"}
      className={!updatedCurrentKeySegment && "flex-center-center"}
    >
      {props.socketComplete && !currentKeySegment && (
        <Alert
          className="flex-center-center"
          variant="text"
          severity="error"
          sx={{ borderRadius: 48 }}
        >
          No keysegments were found. Please try a different query.
        </Alert>
      )}
      {!currentKeySegment && !props.socketComplete && (
        <CircularProgress
          size={36}
          thickness={6}
          sx={{
            color: "#AC1FFF",
          }}
        />
      )}
      {updatedCurrentKeySegment &&  (
        <Box>
          {queryType === 'chatQuery' && (
            <Box mb={'0.5rem'}>
              <span style={{ fontWeight: 600, color: '#AC1FFF' }}>
                {username}: {queryTextRedux}
              </span>
            </Box>
          )}
          <Typography variant="h6" fontWeight={700} mb={'1rem'}>
            {updatedCurrentKeySegment?.headline}
          </Typography>
          <Box
            sx={{
              margin: "0 auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <Box sx={{ width: "85%", height: "20rem" }}>
              <HLSPlayer
                streamUrl={updatedCurrentKeySegment?.url || ""}
                showControls={false}
                playing={playing}
                mute={mute}
                startTime={startTime}
                endTime={endTime}
                height={"300px"}
                width={"100%"}
                currentTime={currentTime}
                handleUpdateCurrentTime={handleUpdateCurrentTime}
                handleAutoPause={handleAutoPause}
              />
            </Box>
            <Box
              sx={{
                marginTop: "10px",
                marginLeft: "30px",
                width: "90%",
              }}
            >
              <Box
                id="player-controls-and-key-moments"
                className="flex-center-center"
                gap="1rem"
              >
                <Box
                  id="player-controls"
                  display="flex"
                  alignItems="center"
                  gap="0.5rem"
                >
                  {playing ? (
                    <PauseCircleFilledOutlined
                      style={{
                        fontSize: 28,
                        cursor: "pointer",
                      }}
                      onClick={() => playPauseHandler(updatedCurrentKeySegment)}
                    />
                  ) : (
                    <PlayCircleFilledOutlined
                      style={{
                        fontSize: 28,
                        cursor: "pointer",
                      }}
                      onClick={() => playPauseHandler(updatedCurrentKeySegment)}
                    />
                  )}

                  {mute ? (
                    <VolumeOffOutlined
                      onClick={() => muteHandler(updatedCurrentKeySegment)}
                      style={{
                        cursor: "pointer",
                        fill: "#AC1FFF",
                      }}
                    />
                  ) : (
                    <VolumeMuteOutlined
                      onClick={() => muteHandler(updatedCurrentKeySegment)}
                      style={{ cursor: "pointer" }}
                    />
                  )}
                  {currentTime >= 0 ? (
                    <Typography
                      variant="caption"
                      sx={{
                        width: "5.5rem",
                      }}
                    >
                      {formatIntervalLabel(Math.floor(currentTime)) +
                        "/" +
                        formatIntervalLabel(Math.floor(videoDuration))}
                    </Typography>
                  ) : (
                    <Typography
                      variant="caption"
                      sx={{
                        width: "5.5rem",
                      }}
                    >
                      --/--
                    </Typography>
                  )}
                </Box>
                {keyMoments?.length > 0 && (
                  <Box
                    id="key-moments"
                    className="flex-center "
                    gap="5px"
                    width="100%"
                    marginRight="3rem"
                  >
                    {keyMoments?.map((moment, index) => (
                      <Box
                        flex={1}
                        sx={{
                          height: "5px",
                          width: "10px",
                          borderRadius: "12px",
                          cursor: "pointer",
                          backgroundColor:
                            moment?.description ===
                            currentKeyMoment?.description
                              ? theme.palette.primary.light
                              : "grey",
                          "&:hover": {
                            backgroundColor: theme.palette.primary.light,
                          },
                        }}
                        onClick={() => onChangeKeyMoment(index)}
                      >
                        {}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
              <Box id="range-slider" display="flex" gap="1rem" alignItems="end">
                <Slider
                  // fullWidth
                  valueLabelDisplay="on"
                  value={sliderValue}
                  valueLabelFormat={(v) => formatIntervalLabel(v)}
                  onChange={handleRangeSlider}
                  // onChangeCommitted={handleRangeCommitted}
                  // min={Math.floor(segmentDetails?.video_start_time / 1000)}
                  min={0}
                  max={Math.floor(
                    updatedCurrentKeySegment?.video_duration / 1000
                  )}
                  step={1}
                  sx={{
                    marginLeft: "0.5rem",
                    padding: 0,
                    marginBottom: 2,
                    marginTop: 1,
                    "& .MuiSlider-thumb": {
                      backgroundColor: "#ffffff",
                      border: "4px solid #666666",
                      height: 12,
                      width: 12,
                    },
                    "& .MuiSlider-rail": {
                      backgroundColor: "#cccccc",
                    },
                    "& .MuiSlider-track": {
                      backgroundColor: "#666666",
                      border: 0,
                    },
                    "& .MuiSlider-mark": {
                      backgroundColor: "#AC1FFF",
                      height: 8,
                    },
                    "& .MuiSlider-valueLabel": {
                      backgroundColor: theme.palette.primary.light,
                      borderRadius: 12,
                      bottom: -54,
                      paddingTop: "2px",
                      paddingBottom: "2px",
                      top: "auto",
                      fontSize: 12,
                      "&::before": {
                        top: -7,
                        bottom: "auto",
                      },
                    },
                  }}
                />
                <IconButton
                  aria-label="reset-keymoment"
                  onClick={handleResetInterval}
                  size="small"
                  sx={{
                    fontSize: 13,
                    color: theme.palette.primary.light,
                    lineHeight: 0,
                    textTransform: "capitalize",
                    fontFamily: "Roboto Condensed",
                    "&:hover": {
                      borderColor: "#666666",
                      color: "#FFFFFF",
                      background: "#666666",
                    },
                  }}
                >
                  <RotateLeft />
                </IconButton>
              </Box>
            </Box>
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={1}
            p={1}
          >
            <Typography color={"textSecondary"} variant="caption">
              {formattedDate(
                updatedCurrentKeySegment?.video_timestamp?.epoch_millis
              )}
            </Typography>
          </Box>
          {currentKeyMoment ? (
            <Typography fontWeight={500} p={"1rem 0"}>
              {currentKeyMoment?.description}
            </Typography>
          ) : (
            <Typography fontWeight={500} p={"1rem 0"}>
              {updatedCurrentKeySegment?.description}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default KeySegmentsNew;
